import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button, MenuItem } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import {
  Copy,
  EllipsisVertical,
  Info,
  LockKeyhole,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';

import { type TypeModel } from '../../../common/dataTypes';
import { createTanstackTable, toastPromise } from '../../../common/utils';
import { useGetProjectById } from '../../../hooks';
import {
  useCloneModel,
  useGetModels,
} from '../../../hooks/tanstack/useModelsHook';
import {
  useGetDistinguishModel,
  useGetRoles,
  useGetRouteParams,
  useGetSearchResults,
} from '../../../hooks/tanstack/useUtilsHook';
import ConfirmDeleteDialog from '../../ConfirmDeleteDialog';
import MenuDropdown from '../../MenuDropdown';
import CreateModelFormDialog from '../../model/CreateModelFormDialog';
import SearchInput from '../../SearchInput';
import TanstackTable from '../../TanstackTable';

export type DataRow = {
  id: number;
  name: string;
  description: string;
  isLocked: boolean;
  dataset: TypeModel['dataset'];
  lastModified: string;
  isBase: boolean;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

const columnHelper = createColumnHelper<DataRow>();

const columns = ({
  handleSelect,
  handleClone,
}: {
  handleSelect: (id: number) => void;
  handleClone: (id: number) => void;
}) => [
  columnHelper.accessor('name', {
    cell: (info) => {
      const isLocked = info.row.original.isLocked;

      const { isShared, isReceived } = useGetDistinguishModel();

      return (
        <div className="group flex items-center gap-2 text-start">
          <div className="flex items-center gap-1">
            <p className="w-full">{info.getValue()}</p>
            {(isLocked || isShared || isReceived) && <LockKeyhole />}
          </div>
        </div>
      );
    },
    header: () => (
      <div className="flex items-start gap-2">
        <p>Model</p>
      </div>
    ),
  }),
  columnHelper.accessor('description', {
    id: 'description',
    cell: (info) => {
      const value = info.getValue();

      return <div className="w-full break-all line-clamp-1">{value}</div>;
    },
    header: () => <div>Description</div>,
  }),
  columnHelper.accessor('dataset', {
    id: 'dataset',
    cell: (info) => {
      const dataset = info.getValue();

      return dataset ? (
        <div className="min-w-[200px]">{dataset.name}</div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      );
    },
    header: () => <div>Dataset</div>,
  }),
  columnHelper.accessor('lastModified', {
    id: 'lastModified',
    cell: (info) => {
      const value = info.getValue();

      const {
        id,
        name,
        description,
        updatedBy,
        isLocked,
        isBase,
        updatedAt,
        dataset,
      } = info.row.original;

      const handleOnclick = () => {
        handleSelect(id);
      };

      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

      const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] =
        useState(false);

      const [openCreateModelDialog, setOpenCreateModelDialog] = useState(false);

      const open = Boolean(anchorEl);

      const handleCloseDropdown = () => setAnchorEl(null);

      const handleOpenConfirmDeleteDialog = () => {
        setOpenConfirmDeleteDialog(true);
      };

      const handleCloseConfirmDeleteDialog = () => {
        setOpenConfirmDeleteDialog(false);
      };

      const handleOpenCreateModelDialog = () => {
        setOpenCreateModelDialog(true);
      };

      const handleCloseCreateModelDialog = () => {
        setOpenCreateModelDialog(false);
      };

      return (
        <div className="flex gap-6 min-w-[200px] justify-between">
          <p className="grow truncate" onClick={handleOnclick}>
            {value
              ? format(value ?? '', 'y/MM/dd hh:mm a') +
                (updatedBy ? ' by ' + updatedBy : '')
              : '--'}
          </p>
          {!isBase && (
            <>
              <MenuDropdown
                trigger={
                  <button>
                    <EllipsisVertical width={20} height={20} />
                  </button>
                }
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                style={{
                  marginTop: '5px',
                }}
                open={open}
              >
                {!isLocked && (
                  <MenuItem
                    onClick={() => {
                      handleCloseDropdown();
                      handleOpenCreateModelDialog();
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <Pencil size={15} />
                      <p>Edit</p>
                    </div>
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    handleClone(id);
                    handleCloseDropdown();
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <Copy size={15} />
                    <p>Clone</p>
                  </div>
                </MenuItem>
                <div className="!border-t !border-[#E4E7EC]" />
                {!isLocked && (
                  <MenuItem
                    onClick={() => {
                      handleCloseDropdown();
                      handleOpenConfirmDeleteDialog();
                    }}
                  >
                    <div className="flex items-center space-x-2 text-primary-red">
                      <Trash2 size={15} />
                      <p>Delete</p>
                    </div>
                  </MenuItem>
                )}
              </MenuDropdown>
              <CreateModelFormDialog
                open={openCreateModelDialog}
                title={'Edit model'}
                handleCloseDialog={handleCloseCreateModelDialog}
                defaultValues={{
                  id,
                  name,
                  description,
                  dataset: dataset.id,
                }}
              />
              <ConfirmDeleteDialog
                type="model"
                row={{
                  id,
                  name,
                  description,
                  modified: updatedAt,
                }}
                open={openConfirmDeleteDialog}
                handleClose={handleCloseConfirmDeleteDialog}
              />
            </>
          )}
        </div>
      );
    },
    sortingFn: (a, b) =>
      new Date(a.original.lastModified as string).getTime() -
      new Date(b.original.lastModified as string).getTime(),
    header: () => <div>Last Modified</div>,
  }),
];

const DefaultModelsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const isAddModelOpen = searchParams.get('add') === 'true';

  const [openCreateModelDialog, setOpenCreateModelDialog] = useState(
    isAddModelOpen ?? false
  );

  const [data, setData] = useState<DataRow[]>([]);

  const { projectRole } = useGetRoles();

  const { projectId, orgId } = useGetRouteParams();

  const form = useForm();

  const { watch } = form;

  const navigate = useNavigate();

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const {
    data: models,
    isLoading: isFetchingModels,
    isRefetching: isRefetchingModels,
  } = useGetModels(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const { mutateAsync: cloneModel, isPending: isCloningModel } =
    useCloneModel();

  const searchText = watch('search');

  const searchResults = useGetSearchResults<TypeModel>({
    searchValue: searchText,
    data: models,
  });

  useEffect(() => {
    if (models) {
      setData(
        (searchResults ?? models).map((r) => ({
          id: r.id,
          name: r.name,
          description: r.description,
          dataset: r.dataset,
          isLocked: r.is_locked,
          isBase: r.is_base,
          updatedAt: r.updated_at,
          createdAt: r.created_at,
          lastModified: r.updated_at,
          createdBy: r.created_by?.full_name,
          updatedBy: r.updated_by?.full_name,
        })) as DataRow[]
      );
    }
  }, [models, searchResults]);

  const handleSelect = (id: number) => {
    navigate(`${id}`);
  };

  const handleClone = async (id: number) => {
    const res = cloneModel({
      modelId: id,
      orgId,
      projectId,
    });

    toastPromise({
      promise: res,
      content: 'Model cloned successfully.',
    });
  };

  const table = createTanstackTable<DataRow>({
    data,
    columns: columns({
      handleSelect,
      handleClone,
    }),

    defaultSorting: {
      id: 'lastModified',
      desc: true,
    },
  });

  const handleOpenCreateModelDialog = () => {
    setOpenCreateModelDialog(true);
  };

  const handleCloseCreateModelDialog = () => {
    searchParams.delete('add');
    setSearchParams(searchParams);

    setOpenCreateModelDialog(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold">Models</h1>
      {project?.is_guest_project && (
        <div className="flex gap-2 items-center">
          <div>
            <Info size={20} color="#666666" />
          </div>
          <p className=" text-[#666] flex items-center font-semibold">
            To work with new models, clone them from the initial model (
            <LockKeyhole size={18} />) in the table below.
          </p>
        </div>
      )}
      <FormProvider {...form}>
        <form className="flex justify-between">
          <div className="flex w-[300px] gap-2">
            <SearchInput />
          </div>
          {projectRole === 'project_owner' && !project?.is_guest_project && (
            <>
              <Button
                variant="contained"
                onClick={handleOpenCreateModelDialog}
                style={{
                  backgroundColor: '#B8341B',
                  textTransform: 'capitalize',
                }}
              >
                <div className="flex items-center gap-2">
                  <Plus width={20} height={20} color="#E8F2FF" />
                  New
                </div>
              </Button>
              <CreateModelFormDialog
                title="Create A New Model"
                open={openCreateModelDialog}
                handleCloseDialog={handleCloseCreateModelDialog}
              />
            </>
          )}
        </form>
      </FormProvider>
      <TanstackTable
        table={table}
        isLoading={isFetchingModels || isRefetchingModels || isCloningModel}
        handleSelect={{
          onClick: handleSelect,
          lockColIndex: 3,
        }}
        emptyState={
          project?.is_guest_project ? (
            <>
              Import your first model from{' '}
              <span
                className="text-[#2196F3] cursor-pointer underline font-semibold"
                onClick={() => {
                  searchParams.set('tab', 'received-models');
                  setSearchParams(searchParams);
                }}
              >
                Received tab
              </span>
            </>
          ) : searchText ? (
            'No results'
          ) : (
            'No models available'
          )
        }
      />
    </div>
  );
};

export default DefaultModelsTable;
